/**
 * Import de correções das divergências (passo 7 — 01/09/2026, pedido do dono):
 * o lado CLIENTE do fluxo export → agente de IA → import. Este módulo é PURO
 * (a rede fica na página):
 *
 *  - `validarArquivoCorrecoes` — valida o envelope do JSON de correções ANTES
 *    de chamar a API (schemaVersao exata, acoes em array); mensagens legíveis
 *    para o dono;
 *  - `lerArquivoCorrecoes` — lê o File do seletor e valida (parse + envelope);
 *  - tipos do relatório do endpoint POST /competencias/:id/correcoes/importar
 *    e os rótulos/cores por status de ação (modal de preview/resultado).
 */

export const SCHEMA_VERSAO_CORRECOES = "divergencias-correcoes@v1";

/** Envelope validado do arquivo de correções (pronto para POST). */
export interface ArquivoCorrecoes {
  schemaVersao: typeof SCHEMA_VERSAO_CORRECOES;
  descricao: string | null;
  aplicar: boolean;
  acoes: Record<string, unknown>[];
}

/** Erro de arquivo legível para o dono (toast/alerta da página). */
export class ErroArquivoCorrecoes extends Error {}

/** Valida o envelope. Lança ErroArquivoCorrecoes com mensagem legível. */
export function validarArquivoCorrecoes(json: unknown): ArquivoCorrecoes {
  if (typeof json !== "object" || json === null || Array.isArray(json)) {
    throw new ErroArquivoCorrecoes("O arquivo não é um JSON de correções (objeto com schemaVersao e acoes).");
  }
  const j = json as Record<string, unknown>;
  if (j.schemaVersao !== SCHEMA_VERSAO_CORRECOES) {
    const veio = typeof j.schemaVersao === "string" ? `"${j.schemaVersao}"` : "ausente";
    throw new ErroArquivoCorrecoes(
      `Este arquivo não é um JSON de correções (schemaVersao ${veio}; esperado "${SCHEMA_VERSAO_CORRECOES}"). ` +
        'Se ele é o EXPORT de divergências ("divergencias-export@v1"), ele vai PARA o agente de IA — ' +
        "importe aqui o arquivo de CORREÇÕES que o agente devolveu.",
    );
  }
  if (!Array.isArray(j.acoes)) {
    throw new ErroArquivoCorrecoes('O JSON de correções precisa ter "acoes" (array de ações — pode ser vazio).');
  }
  const descricao = typeof j.descricao === "string" && j.descricao.trim() ? j.descricao.trim() : null;
  return {
    schemaVersao: SCHEMA_VERSAO_CORRECOES,
    descricao,
    aplicar: false, // a página SEMPRE manda dry-run primeiro; o "aplicar" é a segunda chamada
    acoes: j.acoes as Record<string, unknown>[],
  };
}

/** Lê e valida o arquivo escolhido no seletor (erros de parse viram mensagem legível). */
export async function lerArquivoCorrecoes(file: File): Promise<ArquivoCorrecoes> {
  let texto: string;
  try {
    texto = await file.text();
  } catch {
    throw new ErroArquivoCorrecoes(`Não deu para ler o arquivo "${file.name}".`);
  }
  let json: unknown;
  try {
    json = JSON.parse(texto);
  } catch {
    throw new ErroArquivoCorrecoes(`O arquivo "${file.name}" não é um JSON válido.`);
  }
  try {
    return validarArquivoCorrecoes(json);
  } catch (e) {
    if (e instanceof ErroArquivoCorrecoes) throw new ErroArquivoCorrecoes(`"${file.name}": ${e.message}`);
    throw e;
  }
}

// ---------------------------------------------------------------------------
// Relatório do endpoint (espelho de db/correcoes-import.ts)
// ---------------------------------------------------------------------------

export type StatusAcaoCorrecao = "aplicada" | "aplicavel" | "pulada" | "falhou" | "nao_suportada";

export interface ResultadoAcaoCorrecao {
  indice: number;
  tipo: string;
  status: StatusAcaoCorrecao;
  detalhe: string;
  requerRecalculo?: boolean;
  extras?: Record<string, unknown>;
}

export interface RelatorioCorrecoes {
  competenciaId: number;
  schemaVersao: string;
  descricao: string | null;
  aplicar: boolean;
  totais: {
    total: number;
    validas: number;
    puladas: number;
    falhas: number;
    naoSuportadas: number;
    requerRecalculo: number;
  };
  acoes: ResultadoAcaoCorrecao[];
}

export const ROTULO_TIPO_ACAO: Record<string, string> = {
  justificar: "Justificar divergências",
  lancar_variavel: "Lançar variável",
  excluir_variavel: "Excluir variável",
  override_medias: "Override de médias",
};

export const ROTULO_STATUS_ACAO: Record<StatusAcaoCorrecao, string> = {
  aplicavel: "será aplicada",
  aplicada: "aplicada",
  pulada: "pulada",
  falhou: "falhou",
  nao_suportada: "não suportada",
};

export const COR_STATUS_ACAO: Record<StatusAcaoCorrecao, string> = {
  aplicavel: "sillis",
  aplicada: "green",
  pulada: "gray",
  falhou: "red",
  nao_suportada: "yellow",
};
